// Meta Pixel helper — loads the Facebook pixel script once and exposes
// typed helpers for tracking standard events.

const PIXEL_ID = '1431334005701034'

declare global {
  interface Window {
    fbq?: any
    _fbq?: any
  }
}

let initialized = false

/**
 * Injects the official Meta Pixel base code (once) and fires the initial
 * PageView event. Safe to call multiple times — subsequent calls are no-ops.
 */
export function initMetaPixel(): void {
  if (typeof window === 'undefined' || initialized) return
  initialized = true

  /* eslint-disable */
  ;(function (f: any, b: Document, e: string, v: string) {
    if (f.fbq) return
    const n: any = (f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments)
    })
    if (!f._fbq) f._fbq = n
    n.push = n
    n.loaded = true
    n.version = '2.0'
    n.queue = []
    const t = b.createElement(e) as HTMLScriptElement
    t.async = true
    t.src = v
    // Robust insertion: the classic snippet inserts before the first <script>,
    // but in some bundled / iframe environments no such element exists. Fall
    // back to appending to <head> (or <html>) so we never read .parentNode of
    // undefined.
    const s = b.getElementsByTagName(e)[0]
    if (s && s.parentNode) {
      s.parentNode.insertBefore(t, s)
    } else {
      ;(b.head || b.documentElement).appendChild(t)
    }
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js',
  )
  /* eslint-enable */

  // Disable Meta's automatic event collection (e.g. the auto-tracked
  // "SubscribedButtonClick" that fires on every button/link click). This keeps
  // the pixel reporting ONLY the events we explicitly send (PageView, AddToCart).
  window.fbq('set', 'autoConfig', false, PIXEL_ID)

  window.fbq('init', PIXEL_ID)
  window.fbq('track', 'PageView')
}

/**
 * Fires a Meta Pixel standard event (e.g. 'AddToCart', 'Purchase').
 */
export function trackPixelEvent(
  event: string,
  params?: Record<string, unknown>,
): void {
  if (typeof window === 'undefined' || !window.fbq) return
  window.fbq('track', event, params)
}

/**
 * Returns true once the real fbevents.js library has loaded and is no longer
 * just queueing calls. While loading, fbq exists but events sit in fbq.queue
 * and are not yet sent over the network.
 */
function isPixelReady(): boolean {
  const fbq = (window as any).fbq
  return Boolean(fbq && fbq.loaded && typeof fbq.callMethod === 'function')
}

/**
 * Fires a GUARANTEED Meta Pixel hit via a 1x1 image beacon to the official
 * tracking endpoint (the same approach as the pixel's <noscript> fallback).
 *
 * Why we need this: fbevents.js sends events asynchronously. When a click also
 * navigates the browser away (e.g. to a checkout), the async beacon is often
 * aborted before it leaves — so the event never reaches Meta and only the
 * earlier PageView shows up. An Image() GET request, by contrast, is dispatched
 * synchronously by the browser and reliably survives the navigation.
 *
 * The shared eventID lets Meta de-duplicate this beacon against the fbq event,
 * so the conversion is never double-counted.
 */
function sendPixelImageBeacon(
  event: string,
  params: Record<string, unknown> | undefined,
  eventID: string,
): void {
  try {
    const url = new URL('https://www.facebook.com/tr/')
    url.searchParams.set('id', PIXEL_ID)
    url.searchParams.set('ev', event)
    url.searchParams.set('eid', eventID)
    url.searchParams.set('dl', window.location.href)
    url.searchParams.set('rl', document.referrer || '')
    url.searchParams.set('ts', String(Date.now()))
    url.searchParams.set('noscript', '1')

    // Custom data goes under cd[...] keys.
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url.searchParams.set(
          `cd[${key}]`,
          Array.isArray(value) ? value.join(',') : String(value),
        )
      })
    }

    const img = new Image()
    img.src = url.toString()
  } catch {
    /* never block navigation */
  }
}

/** Generates a reasonably-unique id for event de-duplication. */
function makeEventID(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Fires a tracking event and THEN navigates — but only after giving the pixel
 * a real chance to send the request. This avoids the common problem where an
 * anchor navigates away in the same click tick and the AddToCart beacon is
 * aborted before it leaves the browser (so only PageView ever shows up in
 * Events Manager).
 *
 * Strategy:
 *  1. Poll (briefly) until fbevents.js is actually loaded so the event is sent
 *     immediately instead of sitting in the queue.
 *  2. Fire the event.
 *  3. Wait a short beat for the network beacon, then navigate.
 */
export function trackEventThenNavigate(
  event: string,
  destination: string,
  params?: Record<string, unknown>,
  options?: { maxWaitMs?: number; settleMs?: number },
): void {
  if (typeof window === 'undefined') return

  const settleMs = options?.settleMs ?? 300
  const eventID = makeEventID()

  const go = () => {
    window.location.href = destination
  }

  // 1) Guaranteed delivery: fire a manual image beacon RIGHT AWAY. This GET
  //    request reliably reaches Meta even though we are about to navigate.
  sendPixelImageBeacon(event, params, eventID)

  // 2) Also fire through fbq (when ready) so the event carries full browser /
  //    cookie context. Meta de-duplicates it against the beacon via eventID.
  try {
    if (window.fbq) {
      window.fbq('track', event, params, { eventID })
    }
  } catch {
    /* ignore */
  }

  if (typeof console !== 'undefined') {
    // Visible confirmation in the browser console for QA.
    console.log(`[MetaPixel] ${event} fired`, { eventID, ...params })
  }

  // 3) Give the beacons a brief moment, then navigate to checkout.
  window.setTimeout(go, settleMs)
}
